import {
    IonButton,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToast,
    IonToolbar,
    useIonRouter
} from "@ionic/react";

import {
    arrowDownOutline,
    arrowUpOutline,
    closeOutline,
    diceOutline,
    saveOutline,
    shieldCheckmarkOutline
} from "ionicons/icons";

import { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Cookies from "js-cookie";

import "./FantasyDraftSettings.css";

interface FantasyTeam {
    id: string;
    teamName1?: string;
    name?: string;
    teamName?: string;
    nombre?: string;
}

interface FantasyDraftInfo {
    name?: string;
    date?: string;
    hour?: string;
    time?: string;
    order?: "snake" | "linear" | string;
    draftStartsAt?: string;
}

interface FantasyDraftLeague {
    Info?: FantasyDraftInfo;
    Teams?: Record<string, FantasyTeam>;
    Draft?: unknown[];
    DraftOrder?: string[];
    DraftState?: {
        status?: string;
    };
}

const draftHourOptions = [
    { value: "9", label: "9:00 a. m. CT" },
    { value: "10", label: "10:00 a. m. CT" },
    { value: "11", label: "11:00 a. m. CT" },
    { value: "12", label: "12:00 p. m. CT" },
    { value: "13", label: "1:00 p. m. CT" },
    { value: "14", label: "2:00 p. m. CT" },
    { value: "15", label: "3:00 p. m. CT" },
    { value: "16", label: "4:00 p. m. CT" },
    { value: "17", label: "5:00 p. m. CT" },
    { value: "18", label: "6:00 p. m. CT" },
    { value: "19", label: "7:00 p. m. CT" },
    { value: "20", label: "8:00 p. m. CT" },
    { value: "21", label: "9:00 p. m. CT" },
    { value: "22", label: "10:00 p. m. CT" }
];

const draftPickTimeOptions = ["15", "30", "40", "60"];

function normalizeDraftHour(value: unknown): string {
    const rawValue = String(value || "").trim();

    /*
        Compatibilidad con valores previos:
        "9", "09", "09:00", "18:00".
    */
    const match = rawValue.match(/^([0-9]{1,2})/);

    if (!match) {
        return "";
    }

    const normalizedHour = String(Number(match[1]));

    const isAllowed = draftHourOptions.some(
        function (option) {
            return option.value === normalizedHour;
        }
    );

    return isAllowed ? normalizedHour : "";
}

function getTeamName(
    team: FantasyTeam | undefined,
    fallback: string
): string {
    return String(
        team?.teamName1 ||
        team?.name ||
        team?.teamName ||
        team?.nombre ||
        fallback
    ).trim();
}

function shuffle<T>(items: T[]): T[] {
    const result = [...items];

    for (let index = result.length - 1; index > 0; index--) {
        const randomIndex = Math.floor(
            Math.random() * (index + 1)
        );

        const current = result[index];

        result[index] = result[randomIndex];
        result[randomIndex] = current;
    }

    return result;
}

const FantasyDraftSettings: React.FC = () => {
    const router = useIonRouter();
    const history = useHistory();
    const { id } = useParams<{ id: string }>();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [toastMessage, setToastMessage] = useState("");

    const [leagueName, setLeagueName] = useState("Liga Fantasy");
    const [teams, setTeams] = useState<
        Record<string, FantasyTeam>
    >({});

    const [date, setDate] = useState("");
    const [hour, setHour] = useState("");
    const [time, setTime] = useState("30");
    const [order, setOrder] = useState<"snake" | "linear">(
        "snake"
    );

    const [draftOrder, setDraftOrder] = useState<string[]>([]);
    const [isLocked, setIsLocked] = useState(false);

    function goBack() {
        history.replace(`/fantasySettingsMenu/${id}`);
    }

    function getAccessToken(): string {
        return Cookies.get("access_token") || "";
    }

    function getAccessUsername(): string {
        return Cookies.get("access_username") || "";
    }

    const loadDraftSettings = useCallback(
        async function () {
            try {
                setLoading(true);
                setError("");

                const response = await fetch(
                    `https://fantasy.ftblscore.com/fantasy_data/${id}.json?t=${Date.now()}`,
                    {
                        cache: "no-store"
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        "No fue posible cargar la liga."
                    );
                }

                const league: FantasyDraftLeague =
                    await response.json();

                const info = league.Info || {};
                const loadedTeams = league.Teams || {};
                const teamIds = Object.keys(loadedTeams);

                const savedDraftOrder = Array.isArray(
                    league.DraftOrder
                )
                    ? league.DraftOrder.filter(function (teamId) {
                          return Boolean(loadedTeams[teamId]);
                      })
                    : [];

                /*
                    Se agregan al final equipos que no existían
                    en un DraftOrder antiguo o incompleto.
                */
                const completeDraftOrder = [
                    ...savedDraftOrder,
                    ...teamIds.filter(function (teamId) {
                        return !savedDraftOrder.includes(teamId);
                    })
                ];

                const draftStatus = String(
                    league.DraftState?.status || "scheduled"
                )
                    .trim()
                    .toLowerCase();

                const hasPicks = Array.isArray(league.Draft) &&
                    league.Draft.length > 0;

                setLeagueName(
                    String(info.name || "Liga Fantasy")
                );

                setTeams(loadedTeams);

                setDate(
                    String(info.date || "")
                );

                setHour(
                    normalizeDraftHour(info.hour)
                );


                const loadedTime = String(info.time || "30");

                setTime(
                    draftPickTimeOptions.includes(loadedTime)
                        ? loadedTime
                        : "30"
                );


                setOrder(
                    info.order === "linear"
                        ? "linear"
                        : "snake"
                );

                setDraftOrder(completeDraftOrder);

                setIsLocked(
                    draftStatus === "active" ||
                    draftStatus === "completed" ||
                    hasPicks
                );
            } catch (loadError) {
                console.error(loadError);

                setError(
                    "No fue posible cargar la configuración del draft."
                );
            } finally {
                setLoading(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadDraftSettings();
        },
        [loadDraftSettings]
    );

    function moveTeam(
        teamId: string,
        direction: "up" | "down"
    ) {
        if (isLocked) {
            return;
        }

        setDraftOrder(function (currentOrder) {
            const currentIndex = currentOrder.indexOf(teamId);

            if (currentIndex === -1) {
                return currentOrder;
            }

            const destinationIndex =
                direction === "up"
                    ? currentIndex - 1
                    : currentIndex + 1;

            if (
                destinationIndex < 0 ||
                destinationIndex >= currentOrder.length
            ) {
                return currentOrder;
            }

            const nextOrder = [...currentOrder];

            const currentTeam = nextOrder[currentIndex];

            nextOrder[currentIndex] =
                nextOrder[destinationIndex];

            nextOrder[destinationIndex] = currentTeam;

            return nextOrder;
        });
    }

    function randomizeDraftOrder() {
        if (isLocked) {
            return;
        }

        setDraftOrder(function (currentOrder) {
            return shuffle(currentOrder);
        });

        setToastMessage(
            "Se generó un nuevo orden aleatorio. Guarda los cambios para confirmarlo."
        );
    }

    async function saveDraftSettings() {
        if (isLocked || saving) {
            return;
        }

        const seconds = Number(time);

        const isAllowedHour = draftHourOptions.some(
            function (option) {
                return option.value === hour;
            }
        );

        if (!isAllowedHour) {
            setToastMessage(
                "Selecciona una hora válida para el draft."
            );

            return;
        }

        if (!date || !isAllowedHour) {
            setToastMessage(
                "Selecciona una fecha y una hora válidas para el draft."
            );

            return;
        }

        if (!draftPickTimeOptions.includes(String(seconds))) {
            setToastMessage(
                "El tiempo por selección debe ser 15, 30, 40 o 60 segundos."
            );

            return;
        }


        try {
            setSaving(true);

            const response = await fetch(
                "https://fantasy.ftblscore.com/create_league/" +
                    "control_UpdateFantasyDraftSettings.php",
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        "X-Access-Token": getAccessToken(),
                        "X-Access-Username": getAccessUsername()
                    },
                    body: JSON.stringify({
                        leagueId: id,
                        date,
                        hour,
                        time: seconds,
                        order,
                        draftOrder,
                        orderSource: "manual"
                    })
                }
            );

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                    "No fue posible guardar la configuración."
                );
            }

            setToastMessage(
                "Configuración del draft guardada correctamente."
            );
        } catch (saveError) {
            console.error(saveError);

            setToastMessage(
                saveError instanceof Error
                    ? saveError.message
                    : "No fue posible guardar la configuración."
            );
        } finally {
            setSaving(false);
        }
    }

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="draft-settings-toolbar">
                    <IonButton
                        slot="start"
                        fill="clear"
                        color="medium"
                        aria-label="Regresar a configuración"
                        onClick={goBack}
                    >
                        <IonIcon icon={closeOutline} />
                    </IonButton>

                    <IonTitle>Configuración del draft</IonTitle>

                    <IonButton
                        slot="end"
                        fill="clear"
                        disabled={loading || saving || isLocked}
                        aria-label="Guardar configuración"
                        onClick={saveDraftSettings}
                    >
                        <IonIcon icon={saveOutline} />
                    </IonButton>
                </IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                className="draft-settings-content"
            >
                <section className="draft-settings-hero">
                    <div className="draft-settings-hero__icon">
                        <IonIcon icon={shieldCheckmarkOutline} />
                    </div>

                    <div>
                        <span>COMISIONADO</span>
                        <h1>{leagueName}</h1>
                        <p>
                            Configura el inicio, modalidad y orden
                            de selección.
                        </p>
                    </div>
                </section>

                {loading ? (
                    <IonProgressBar
                        className="draft-settings-progress"
                        type="indeterminate"
                    />
                ) : null}

                {!loading && error ? (
                    <main className="draft-settings-main">
                        <div className="draft-settings-empty">
                            <strong>No pudimos cargar el draft</strong>
                            <span>{error}</span>

                            <IonButton
                                size="small"
                                fill="outline"
                                onClick={loadDraftSettings}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    </main>
                ) : null}

                {!loading && !error ? (
                    <main className="draft-settings-main">
                        {isLocked ? (
                            <div className="draft-settings-locked">
                                <strong>
                                    El draft ya está bloqueado
                                </strong>

                                <p>
                                    No es posible modificar fecha,
                                    modalidad ni orden porque el draft
                                    ya inició o tiene selecciones.
                                </p>
                            </div>
                        ) : (
                            <div className="draft-settings-notice">
                                Guarda los cambios antes de salir de
                                esta pantalla.
                            </div>
                        )}

                        <section className="draft-settings-section">
                            <span className="draft-settings-label">
                                HORARIO DEL DRAFT
                            </span>

                            <div className="draft-settings-card">
                                <label>
                                    <span>Fecha</span>

                                    <input
                                        type="date"
                                        value={date}
                                        disabled={isLocked}
                                        onChange={function (event) {
                                            setDate(event.target.value);
                                        }}
                                    />
                                </label>

                                <label>
                                    <span>Hora</span>

                                    <IonSelect
                                        value={hour}
                                        disabled={isLocked}
                                        interface="action-sheet"
                                        placeholder="Selecciona una hora"
                                        onIonChange={function (event) {
                                            setHour(
                                                normalizeDraftHour(event.detail.value)
                                            );
                                        }}
                                    >
                                        {draftHourOptions.map(function (option) {
                                            return (
                                                <IonSelectOption
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </IonSelectOption>
                                            );
                                        })}
                                    </IonSelect>
                                </label>


                                <label>
                                    <span>
                                        Segundos por selección
                                    </span>

                                    <input
                                        type="number"
                                        min="10"
                                        max="300"
                                        step="5"
                                        value={time}
                                        disabled={isLocked}
                                        onChange={function (event) {
                                            setTime(event.target.value);
                                        }}
                                    />
                                </label>
                            </div>
                        </section>

                        <section className="draft-settings-section">
                            <span className="draft-settings-label">
                                MODALIDAD
                            </span>

                            <div className="draft-settings-format">
                                <button
                                    type="button"
                                    disabled={isLocked}
                                    className={
                                        order === "snake"
                                            ? "draft-settings-format__option draft-settings-format__option--selected"
                                            : "draft-settings-format__option"
                                    }
                                    onClick={function () {
                                        setOrder("snake");
                                    }}
                                >
                                    <strong>Snake</strong>

                                    <small>
                                        El orden se invierte en cada
                                        ronda.
                                    </small>
                                </button>

                                <button
                                    type="button"
                                    disabled={isLocked}
                                    className={
                                        order === "linear"
                                            ? "draft-settings-format__option draft-settings-format__option--selected"
                                            : "draft-settings-format__option"
                                    }
                                    onClick={function () {
                                        setOrder("linear");
                                    }}
                                >
                                    <strong>Lineal</strong>

                                    <small>
                                        El mismo orden se mantiene en
                                        todas las rondas.
                                    </small>
                                </button>
                            </div>
                        </section>

                        <section className="draft-settings-section">
                            <div className="draft-settings-order-heading">
                                <div>
                                    <span className="draft-settings-label">
                                        ORDEN DE SELECCIÓN
                                    </span>

                                    <p>
                                        Ajusta manualmente la posición
                                        de cada equipo.
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    className="draft-settings-random"
                                    disabled={isLocked}
                                    onClick={randomizeDraftOrder}
                                >
                                    <IonIcon icon={diceOutline} />
                                    Aleatorio
                                </button>
                            </div>

                            <div className="draft-settings-order">
                                {draftOrder.map(function (
                                    teamId,
                                    index
                                ) {
                                    const team = teams[teamId];

                                    return (
                                        <article
                                            className="draft-settings-order__item"
                                            key={teamId}
                                        >
                                            <strong>
                                                {index + 1}
                                            </strong>

                                            <span>
                                                {getTeamName(
                                                    team,
                                                    "Equipo"
                                                )}
                                            </span>

                                            <div>
                                                <button
                                                    type="button"
                                                    disabled={
                                                        isLocked ||
                                                        index === 0
                                                    }
                                                    aria-label="Subir equipo"
                                                    onClick={function () {
                                                        moveTeam(
                                                            teamId,
                                                            "up"
                                                        );
                                                    }}
                                                >
                                                    <IonIcon
                                                        icon={
                                                            arrowUpOutline
                                                        }
                                                    />
                                                </button>

                                                <button
                                                    type="button"
                                                    disabled={
                                                        isLocked ||
                                                        index ===
                                                            draftOrder.length -
                                                                1
                                                    }
                                                    aria-label="Bajar equipo"
                                                    onClick={function () {
                                                        moveTeam(
                                                            teamId,
                                                            "down"
                                                        );
                                                    }}
                                                >
                                                    <IonIcon
                                                        icon={
                                                            arrowDownOutline
                                                        }
                                                    />
                                                </button>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        </section>

                        <IonButton
                            expand="block"
                            className="draft-settings-save"
                            disabled={isLocked || saving}
                            onClick={saveDraftSettings}
                        >
                            {saving
                                ? "Guardando..."
                                : "Guardar configuración"}

                            <IonIcon
                                slot="end"
                                icon={saveOutline}
                            />
                        </IonButton>
                    </main>
                ) : null}
            </IonContent>

            <IonToast
                isOpen={Boolean(toastMessage)}
                message={toastMessage}
                duration={3000}
                position="bottom"
                onDidDismiss={function () {
                    setToastMessage("");
                }}
            />
        </IonPage>
    );
};

export default FantasyDraftSettings;
